/**
 * جلسة المستخدم في الواجهة.
 *
 * الرمز الوحيد المخزّن محلياً هو رمز الجلسة (HMAC bearer من الـCore).
 * لا كلمات مرور ولا أسرار أخرى في المتصفح.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, getToken, setToken } from '@/lib/api';

export interface SessionUser {
  id: string;
  phone: string;
  name: string;
}

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthValue {
  status: AuthStatus;
  user: SessionUser | null;
  requestCode: (phone: string) => Promise<{ devCode?: string }>;
  verifyCode: (phone: string, code: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  updateName: (name: string) => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!getToken()) {
      setStatus('anonymous');
      return;
    }
    api<{ user: SessionUser }>('/auth/me')
      .then((res) => {
        if (cancelled) return;
        setUser(res.user);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled) return;
        setToken(null);
        setStatus('anonymous');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const requestCode = useCallback(async (phone: string) => {
    const res = await api<{ ok: boolean; dev_code?: string }>('/auth/otp/request', {
      method: 'POST',
      body: { phone },
      auth: false,
    });
    return res.dev_code ? { devCode: res.dev_code } : {};
  }, []);

  const verifyCode = useCallback(async (phone: string, code: string, name?: string) => {
    const res = await api<{ token: string; user: SessionUser }>('/auth/otp/verify', {
      method: 'POST',
      body: { phone, code, name },
      auth: false,
    });
    setToken(res.token);
    setUser(res.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      /* الجلسة عديمة الحالة — الخروج محلي على أي حال */
    }
    setToken(null);
    setUser(null);
    setStatus('anonymous');
  }, []);

  const updateName = useCallback(async (name: string) => {
    const res = await api<{ user: SessionUser }>('/auth/profile', {
      method: 'PATCH',
      body: { name },
    });
    setUser(res.user);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ status, user, requestCode, verifyCode, logout, updateName }),
    [status, user, requestCode, verifyCode, logout, updateName],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}
